import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Button,
  Spinner,
} from '../../components/design-system';
import { computeTripPricing, quoteTrip, type TripPricing } from '../../../supabase/functions/_shared/pricing.ts';
import { MultiCitySwitch } from '../../components/admin/MultiCitySwitch';
import type { PlatformSettings } from '../../types';

function money(cents: number, currency: string): string {
  return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
}

export function AdminPricing() {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const [vat, setVat] = useState(23);
  const [hostShare, setHostShare] = useState(65);
  const [basePrice, setBasePrice] = useState(47);
  const [extraPrice, setExtraPrice] = useState(10);
  const [currency, setCurrency] = useState('eur');
  const [companyName, setCompanyName] = useState('');
  const [companyNif, setCompanyNif] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [minHosts, setMinHosts] = useState(2);
  useEffect(() => {
    supabase
      .from('platform_settings')
      .select('*')
      .limit(1)
      .single()
      .then(({ data }) => {
        if (data) {
          const s = data as PlatformSettings;
          setSettings(s);
          setVat(s.vat_percentage ?? 23);
          setHostShare(s.host_share_percentage ?? 65);
          setBasePrice(s.base_day_price_cents / 100);
          setExtraPrice(s.extra_day_price_cents / 100);
          setCurrency(s.currency);
          setCompanyName(s.company_legal_name ?? '');
          setCompanyNif(s.company_nif ?? '');
          setCompanyAddress(s.company_address ?? '');
          setMinHosts(s.min_hosts_per_city ?? 2);
        }
        setLoading(false);
      });
  }, []);

  async function handleSave() {
    if (!settings) return;
    setSaving(true);
    setSaved(false);
    setSaveError(false);

    const { error } = await supabase
      .from('platform_settings')
      .update({
        vat_percentage: vat,
        host_share_percentage: hostShare,
        base_day_price_cents: Math.round(basePrice * 100),
        extra_day_price_cents: Math.round(extraPrice * 100),
        currency,
        company_legal_name: companyName.trim() || null,
        company_nif: companyNif.trim() || null,
        company_address: companyAddress.trim() || null,
        min_hosts_per_city: minHosts,
        updated_at: new Date().toISOString(),
      })
      .eq('id', settings.id);

    setSaving(false);
    if (error) {
      console.error('platform_settings save failed:', error);
      setSaveError(true);
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  if (loading) {
    return (
      <div className="min-h-dvh bg-app-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const previewDays = 3;
  let preview: TripPricing | null = null;
  try {
    preview = computeTripPricing(
      Math.round(basePrice * 100) + (previewDays - 1) * Math.round(extraPrice * 100),
      vat,
      hostShare,
    );
  } catch {
    preview = null; // a field is empty or out of range: say so instead of showing nonsense
  }

  // A trip with two cities is priced city by city: the first day of each city
  // costs the base price. Shown so the effect of the prices is visible.
  let twoCities: { perCity: number; oneStay: number } | null = null;
  try {
    const input = { legs: [{ days: 3 }, { days: 4 }], tripDays: 7, baseCents: Math.round(basePrice * 100), extraCents: Math.round(extraPrice * 100) };
    twoCities = {
      perCity: quoteTrip({ ...input, perCity: true }).grossCents,
      oneStay: quoteTrip({ ...input, perCity: false }).grossCents,
    };
  } catch {
    twoCities = null;
  }

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12">
        <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Pricing</h1>
        <p className="text-app-muted text-sm mb-8">Configure trip pricing, VAT and the host's share.</p>

        <div className="flex flex-col gap-6">
          <div className="bg-app-card rounded-xl border border-app-border p-5 flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-app-fg uppercase tracking-wide">Trip pricing (VAT included)</h2>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm text-app-muted">First day</label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={1}
                    value={basePrice}
                    onChange={(e) => setBasePrice(Number(e.target.value))}
                    className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg"
                  />
                  <span className="text-sm text-app-muted uppercase">{currency}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm text-app-muted">Each extra day</label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={0}
                    value={extraPrice}
                    onChange={(e) => setExtraPrice(Number(e.target.value))}
                    className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg"
                  />
                  <span className="text-sm text-app-muted uppercase">{currency}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm text-app-muted">Currency</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg w-32"
              >
                <option value="eur">EUR</option>
                <option value="usd">USD</option>
                <option value="gbp">GBP</option>
              </select>
            </div>
          </div>

          <div className="bg-app-card rounded-xl border border-app-border p-5 flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-app-fg uppercase tracking-wide">VAT and host share</h2>

            <div className="flex flex-col gap-1">
              <label htmlFor="vat" className="text-sm text-app-muted">VAT included in the price</label>
              <div className="flex items-center gap-1">
                <input
                  id="vat"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={vat}
                  onChange={(e) => setVat(Number(e.target.value))}
                  className="w-24 bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg"
                />
                <span className="text-sm text-app-muted">%</span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="host-share" className="text-sm text-app-muted">Host share, as a % of the price without VAT</label>
              <div className="flex items-center gap-3">
                <input
                  id="host-share"
                  type="range"
                  min={0}
                  max={100}
                  value={hostShare}
                  onChange={(e) => setHostShare(Number(e.target.value))}
                  className="flex-1 accent-gold"
                />
                <span className="text-lg font-semibold text-gold w-14 text-right">{hostShare}%</span>
              </div>
            </div>
            <p className="text-xs text-app-muted">
              The host is paid this share against their invoice. If the host charges VAT, it is inside this amount.
              Changes apply to new trips only. Existing trips keep the split they were booked with.
            </p>
          </div>

          <div className="bg-app-card rounded-xl border border-app-border p-5 flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-app-fg uppercase tracking-wide">Invoicing details</h2>
            <p className="text-xs text-app-muted">
              Shown to hosts when they send an invoice, so they can address their recibo to Sekretly.
            </p>
            <div className="flex flex-col gap-1">
              <label htmlFor="company-name" className="text-sm text-app-muted">Legal name</label>
              <input id="company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg" />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="company-nif" className="text-sm text-app-muted">NIF</label>
              <input id="company-nif" value={companyNif} onChange={(e) => setCompanyNif(e.target.value)}
                className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg" />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="company-address" className="text-sm text-app-muted">Address</label>
              <textarea id="company-address" rows={2} value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)}
                className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg" />
            </div>
          </div>

          <div className="bg-gold/8 border border-gold/25 rounded-xl p-4 flex flex-col gap-2">
            <p className="text-xs font-medium text-gold uppercase tracking-wide">
              Preview: {previewDays}-day trip
            </p>
            {preview ? (
              <div className="flex flex-col gap-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-app-muted">Traveler pays (VAT included)</span>
                  <span className="text-app-fg font-medium">{money(preview.grossCents, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-app-muted">VAT ({preview.vatRate}%)</span>
                  <span className="text-app-fg">{money(preview.vatCents, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-app-muted">Price without VAT</span>
                  <span className="text-app-fg">{money(preview.netCents, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-app-muted">Host is paid ({preview.hostShareRate}%)</span>
                  <span className="text-emerald-500 font-medium">{money(preview.hostFeeCents, currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-app-muted">Sekretly keeps</span>
                  <span className="text-app-fg font-medium">{money(preview.commissionCents, currency)}</span>
                </div>
              </div>
            ) : (
              <p role="alert" className="text-sm text-crimson">Enter whole percentages from 0 to 100 and valid prices to see the split.</p>
            )}
            {twoCities && (
              <p className="text-xs text-app-muted pt-2 border-t border-gold/25">
                Two cities (3 + 4 days) cost {money(twoCities.perCity, currency)}, because the first day of each city costs the base price.
                The same 7 days in one city cost {money(twoCities.oneStay, currency)}.
              </p>
            )}
          </div>

          <div className="bg-app-card rounded-xl border border-app-border p-5 flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-app-fg uppercase tracking-wide">Cities</h2>
            <div className="flex flex-col gap-1">
              <label htmlFor="min-hosts" className="text-sm text-app-muted">Hosts a city needs before it can be booked</label>
              <input
                id="min-hosts"
                type="number"
                min={1}
                max={20}
                value={minHosts}
                onChange={(e) => setMinHosts(Number(e.target.value))}
                className="w-24 bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg"
              />
            </div>
            <p className="text-xs text-app-muted">
              Counts hosts who are active, not suspended, have billing details and cover the city. A city with fewer shows
              as "coming soon" to travelers. This applies once trips with several cities are switched on (for admins while testing).
            </p>
          </div>

          <MultiCitySwitch
            settingsId={settings!.id}
            initial={settings?.per_city_trips === true}
            initialAdmins={settings?.per_city_trips_admins === true}
          />

          {saveError ? (
            <p role="alert" className="text-sm text-crimson">We couldn't save these settings. Check the values and try again.</p>
          ) : null}
          <Button onClick={() => void handleSave()} disabled={saving || !preview || !Number.isInteger(minHosts) || minHosts < 1 || minHosts > 20}>
            {saving ? <Spinner size="sm" className="text-ink" /> : saved ? 'Saved!' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AdminPricing;
